import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Check, AlertCircle } from 'lucide-react';
import { Button, Card, Loading } from '../../components/common';
import tenantsService from '../../services/api/tenants.service';
import authService from '../../services/api/auth.service';

const WhatsAppOnboarding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('initial'); // initial, connecting, success, error

  // Obtém dados do tenant logado
  const currentUser = authService.getUserData();
  
  // Facebook App ID e Configuration ID
  const FB_APP_ID = process.env.REACT_APP_FB_APP_ID;
  const CONFIGURATION_ID = process.env.REACT_APP_FB_CONFIG_ID;

  useEffect(() => {
    // Verifica se é tenant (não admin)
    if (!currentUser || currentUser.type !== 'tenant') {
      navigate('/login');
      return;
    }

    // Verifica se as variáveis de ambiente estão configuradas
    if (!FB_APP_ID || !CONFIGURATION_ID) {
      setError('Configuração do Facebook não encontrada. Verifique o arquivo .env');
      setStep('error');
      return;
    }

    // Carregar SDK do Facebook
    loadFacebookSDK();
  }, []);

  const loadFacebookSDK = () => {
    // Verifica se já existe
    if (window.FB) {
      return;
    }

    // Adiciona div necessária para o SDK
    if (!document.getElementById('fb-root')) {
      const fbRoot = document.createElement('div');
      fbRoot.id = 'fb-root';
      document.body.appendChild(fbRoot);
    }

    // Inicializa SDK
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: FB_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v21.0'
      });
    };

    // Carrega SDK
    (function(d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  };

  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      setError('Facebook SDK não carregado. Recarregue a página.');
      return;
    }

    setLoading(true);
    setStep('connecting');
    setError('');

    // Chama o Embedded Signup Flow
    window.FB.login(
      function(response) {
        if (response.authResponse) {
          // Sucesso - obtém os dados da sessão
          const authResponse = response.authResponse;
          
          // Processa os dados retornados
          handleFacebookResponse(authResponse);
        } else {
          setLoading(false);
          setStep('error');
          setError('Login cancelado ou falhou. Tente novamente.');
        }
      },
      {
        config_id: CONFIGURATION_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: '',
          sessionInfoVersion: 2
        }
      }
    );
  };

  const handleFacebookResponse = async (authResponse) => {
    try {
      // Dados retornados pelo Facebook no authResponse
      console.log('Auth Response:', authResponse);

      // O authResponse contém:
      // - accessToken: token de curta duração
      // - code: código para trocar por token de longa duração
      // - grantedScopes: permissões concedidas
      
      // Obtém informações adicionais via Graph API
      window.FB.api(
        '/me',
        { 
          fields: 'name,id',
          access_token: authResponse.accessToken 
        },
        async function(userResponse) {
          if (userResponse && !userResponse.error) {
            // Obtém informações do WABA (WhatsApp Business Account)
            window.FB.api(
              `/${userResponse.id}/businesses`,
              { access_token: authResponse.accessToken },
              async function(businessResponse) {
                if (businessResponse && !businessResponse.error && businessResponse.data) {
                  const business = businessResponse.data[0];
                  
                  // Obtém o Phone Number ID
                  window.FB.api(
                    `/${business.id}/owned_whatsapp_business_accounts`,
                    { access_token: authResponse.accessToken },
                    async function(wabaResponse) {
                      if (wabaResponse && !wabaResponse.error && wabaResponse.data) {
                        const waba = wabaResponse.data[0];
                        
                        // Obtém Phone Number
                        window.FB.api(
                          `/${waba.id}/phone_numbers`,
                          { access_token: authResponse.accessToken },
                          async function(phoneResponse) {
                            if (phoneResponse && !phoneResponse.error && phoneResponse.data) {
                              const phone = phoneResponse.data[0];
                              
                              // Salva os dados no tenant
                              await saveWhatsAppData({
                                accessToken: authResponse.accessToken,
                                phoneNumberId: phone.id,
                                phoneNumber: phone.display_phone_number,
                                businessName: business.name || userResponse.name,
                                wabaId: waba.id
                              });
                            } else {
                              throw new Error('Erro ao obter número de telefone');
                            }
                          }
                        );
                      } else {
                        throw new Error('Erro ao obter WABA');
                      }
                    }
                  );
                } else {
                  throw new Error('Erro ao obter informações do negócio');
                }
              }
            );
          } else {
            throw new Error('Erro ao obter informações do usuário');
          }
        }
      );

    } catch (err) {
      console.error('Erro ao processar resposta do Facebook:', err);
      setError(err.message || 'Erro ao processar dados do WhatsApp');
      setStep('error');
      setLoading(false);
    }
  };

  const saveWhatsAppData = async (whatsappData) => {
    try {
      // 1. Busca o App Secret do backend de forma segura (requer autenticação)
      const config = await tenantsService.getWhatsAppConfig(currentUser.tenantId);
      
      // 2. Atualiza o tenant com as credenciais do WhatsApp
      // Endpoint: PATCH /tenants/:id
      await tenantsService.update(currentUser.tenantId, {
        waPhoneNumberId: whatsappData.phoneNumberId,
        waAccessToken: whatsappData.accessToken,
        waAppSecret: config.waAppSecret, // App Secret vem do backend
        // Atualiza o nome se veio do Facebook
        ...(whatsappData.businessName && { name: whatsappData.businessName }),
      });

      setStep('success');
      setLoading(false);

      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (err) {
      console.error('Erro ao salvar dados:', err);
      setError(err.message || 'Erro ao salvar configurações do WhatsApp');
      setStep('error');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <Card.Body className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-400 to-primary-500 rounded-2xl mb-4 shadow-glow">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 font-display">
              Conectar WhatsApp Business
            </h1>
            <p className="text-gray-600">
              Configure sua conta do WhatsApp Business em poucos cliques
            </p>
          </div>

          {/* Initial State */}
          {step === 'initial' && (
            <div className="space-y-6">
              {/* Features */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Configuração Rápida
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Conecte sua conta do WhatsApp Business em menos de 2 minutos
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Automação Inteligente
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Chatbots com IA para atender seus clientes 24/7
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Gestão Centralizada
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Acompanhe todas as conversas em um único dashboard
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="pt-4">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={launchWhatsAppSignup}
                  disabled={loading || !FB_APP_ID || !CONFIGURATION_ID}
                >
                  Conectar WhatsApp Business
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Ao continuar, você será redirecionado para o Facebook para autorizar a conexão
                </p>
              </div>
            </div>
          )}

          {/* Connecting State */}
          {step === 'connecting' && (
            <div className="text-center py-8">
              <Loading size="lg" variant="dots" />
              <p className="text-gray-700 font-medium mt-4">
                Conectando sua conta...
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Aguarde enquanto processamos sua autorização
              </p>
            </div>
          )}

          {/* Success State */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Conectado com Sucesso!
              </h2>
              <p className="text-gray-600 mb-6">
                Sua conta do WhatsApp Business foi conectada à plataforma
              </p>
              <div className="animate-pulse text-primary-600 font-medium">
                Redirecionando para o dashboard...
              </div>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Erro na Conexão
              </h2>
              <p className="text-gray-600 mb-6">
                {error}
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  setStep('initial');
                  setError('');
                }}
              >
                Tentar Novamente
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default WhatsAppOnboarding;